import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  MessageCircle,
  Settings,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Eye,
  EyeOff,
  FileText,
  Send,
} from 'lucide-react';
import api from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Panel, Card, Badge, Button, buttonClass } from '../../design-system';

const inputClass =
  'w-full h-10 rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 focus:outline-none';
const labelClass = 'block text-sm font-medium text-slate-700 mb-1';

const configSchema = z.object({
  evolutionUrl: z.string().min(1, 'URL da Evolution API obrigatoria').url('URL invalida'),
  evolutionApiKey: z.string().min(1, 'API Key obrigatoria'),
  instanceName: z.string().min(1, 'Nome da instancia obrigatorio'),
});

type ConfigFormData = z.infer<typeof configSchema>;

interface WhatsAppStatus {
  conectado: boolean;
  estado?: string;
  mensagem: string;
}

interface WhatsAppConfigData {
  configurado: boolean;
  evolutionUrl?: string;
  instanceName?: string;
  ativo?: boolean;
  dataConfiguracao?: string;
}

interface WhatsAppTemplate {
  id: number;
  nome: string;
  categoria: string;
  conteudo: string;
  ativo: boolean;
  dataCadastro: string;
}

export default function WhatsAppConfig() {
  const { showSuccess, showApiError } = useNotification();
  const [config, setConfig] = useState<WhatsAppConfigData | null>(null);
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [activeTab, setActiveTab] = useState<'config' | 'templates' | 'teste'>('config');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConfigFormData>({
    resolver: zodResolver(configSchema),
  });

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, []);

  const loadData = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const [configRes, templatesRes] = await Promise.all([
        api.get<WhatsAppConfigData>('/whatsapp/config', { signal }),
        api.get<WhatsAppTemplate[]>('/whatsapp/templates', { signal }),
      ]);

      setConfig(configRes.data);
      setTemplates(templatesRes.data);

      if (configRes.data.configurado) {
        reset({
          evolutionUrl: configRes.data.evolutionUrl || '',
          evolutionApiKey: '',
          instanceName: configRes.data.instanceName || '',
        });
        checkStatus(signal);
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'CanceledError') {
        showApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const checkStatus = async (signal?: AbortSignal) => {
    try {
      setCheckingStatus(true);
      const response = await api.get<WhatsAppStatus>('/whatsapp/status', { signal });
      setStatus(response.data);
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'CanceledError') {
        setStatus({ conectado: false, mensagem: 'Erro ao verificar status' });
      }
    } finally {
      setCheckingStatus(false);
    }
  };

  const onSubmit = async (data: ConfigFormData) => {
    try {
      setSaving(true);
      await api.post('/whatsapp/config', data);
      showSuccess('Configuracao salva com sucesso');
      loadData();
    } catch (error) {
      showApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const getCategoriaTone = (categoria: string): 'neutral' | 'success' | 'info' | 'danger' | 'primary' => {
    const tones: Record<string, 'neutral' | 'success' | 'info' | 'danger' | 'primary'> = {
      GERAL: 'neutral',
      VENDA: 'success',
      LEAD: 'info',
      COBRANCA: 'danger',
      AGENDAMENTO: 'primary',
    };
    return tones[categoria] || 'neutral';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-slate-50 text-slate-400 ring-1 ring-slate-100">
            <MessageCircle className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">WhatsApp</h1>
            <p className="text-sm text-slate-500">Integracao via Evolution API</p>
          </div>
        </div>

        {/* Status Badge */}
        {config?.configurado && (
          <div className="flex items-center gap-2">
            {checkingStatus ? (
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            ) : status?.conectado ? (
              <Badge tone="success">
                <CheckCircle className="w-3.5 h-3.5" />
                Conectado
              </Badge>
            ) : (
              <Badge tone="danger">
                <XCircle className="w-3.5 h-3.5" />
                Desconectado
              </Badge>
            )}
            <button
              onClick={() => checkStatus()}
              disabled={checkingStatus}
              className="grid h-9 w-9 place-items-center rounded-xl text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
              title="Verificar status"
            >
              <RefreshCw className={`w-5 h-5 ${checkingStatus ? 'animate-spin' : ''}`} />
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('config')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'config'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Settings className="w-4 h-4 inline-block mr-2" />
            Configuracao
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'templates'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <FileText className="w-4 h-4 inline-block mr-2" />
            Templates ({templates.filter(t => t.ativo).length})
          </button>
          <button
            onClick={() => setActiveTab('teste')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'teste'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Send className="w-4 h-4 inline-block mr-2" />
            Teste
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'config' && (
        <Panel title="Configuracao da Evolution API" icon={<Settings className="h-4 w-4" />}>
          <p className="text-sm text-slate-500 mb-6">
            Configure a conexao com sua instancia da Evolution API para enviar mensagens pelo WhatsApp.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className={labelClass}>
                URL da Evolution API
              </label>
              <input
                type="url"
                {...register('evolutionUrl')}
                placeholder="https://sua-evolution-api.com"
                className={inputClass}
              />
              {errors.evolutionUrl && (
                <p className="mt-1 text-sm text-rose-600">{errors.evolutionUrl.message}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>
                API Key
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  {...register('evolutionApiKey')}
                  placeholder={config?.configurado ? '••••••••••••••••' : 'Sua API Key'}
                  className={`${inputClass} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.evolutionApiKey && (
                <p className="mt-1 text-sm text-rose-600">{errors.evolutionApiKey.message}</p>
              )}
              {config?.configurado && (
                <p className="mt-1 text-xs text-slate-500">
                  Deixe em branco para manter a API Key atual
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>
                Nome da Instancia
              </label>
              <input
                type="text"
                {...register('instanceName')}
                placeholder="minha-instancia"
                className={inputClass}
              />
              {errors.instanceName && (
                <p className="mt-1 text-sm text-rose-600">{errors.instanceName.message}</p>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Salvar Configuracao
              </Button>
            </div>
          </form>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-sky-50 rounded-xl border border-sky-200">
            <h3 className="text-sm font-semibold text-sky-800 mb-2">Como obter a Evolution API?</h3>
            <ul className="text-sm text-sky-700 space-y-1">
              <li>1. Acesse o repositorio oficial em github.com/EvolutionAPI/evolution-api</li>
              <li>2. Instale e configure sua propria instancia</li>
              <li>3. Crie uma instancia e conecte seu WhatsApp via QR Code</li>
              <li>4. Copie a URL e API Key para configurar aqui</li>
            </ul>
          </div>
        </Panel>
      )}

      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold tracking-tight text-slate-900">Templates de Mensagem</h2>
            <button className={buttonClass('primary')}>
              + Novo Template
            </button>
          </div>

          <div className="grid gap-4">
            {templates.filter(t => t.ativo).map(template => (
              <Card key={template.id} className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="space-y-1.5">
                    <h3 className="font-semibold text-slate-900">{template.nome}</h3>
                    <Badge tone={getCategoriaTone(template.categoria)}>
                      {template.categoria}
                    </Badge>
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl text-sm text-slate-700 whitespace-pre-wrap">
                  {template.conteudo}
                </div>
              </Card>
            ))}

            {templates.filter(t => t.ativo).length === 0 && (
              <div className="text-center py-8 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum template cadastrado</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'teste' && (
        <TesteEnvio disabled={!config?.configurado || !status?.conectado} />
      )}
    </div>
  );
}

// Componente de Teste de Envio
function TesteEnvio({ disabled }: { disabled: boolean }) {
  const { showSuccess, showApiError } = useNotification();
  const [telefone, setTelefone] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleEnviar = async () => {
    if (!telefone || !mensagem) return;

    try {
      setEnviando(true);
      await api.post('/whatsapp/enviar', { telefone, mensagem });
      showSuccess('Mensagem enviada com sucesso!');
      setMensagem('');
    } catch (error) {
      showApiError(error);
    } finally {
      setEnviando(false);
    }
  };

  const inputClass =
    'w-full h-10 rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 focus:outline-none';
  const labelClass = 'block text-sm font-medium text-slate-700 mb-1';

  return (
    <Panel title="Teste de Envio" icon={<Send className="h-4 w-4" />}>
      {disabled ? (
        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 text-amber-800">
          <p className="text-sm">
            Configure e conecte o WhatsApp antes de enviar mensagens de teste.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className={labelClass}>
              Telefone (com DDD)
            </label>
            <input
              type="tel"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              placeholder="11999999999"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>
              Mensagem
            </label>
            <textarea
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              rows={4}
              placeholder="Digite sua mensagem de teste..."
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 focus:outline-none"
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleEnviar}
              disabled={enviando || !telefone || !mensagem}
            >
              {enviando ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Enviar Mensagem
            </Button>
          </div>
        </div>
      )}
    </Panel>
  );
}
